import { composeRenderProps, ModalOverlay, ModalOverlayProps } from "react-aria-components";
import { twMerge } from "tailwind-merge";

export interface ModalBackdropProps extends ModalOverlayProps {
  /**
   * Whether to use a transparent backdrop instead of dark overlay
   * @default false
   */
  transparent?: boolean;
  /**
   * Additional CSS classes for the backdrop
   */
  className?: string;
}

/**
 * ModalBackdrop
 *
 * Shared backdrop/overlay component used by Dialog and Drawer.
 * Wraps React Aria's ModalOverlay with consistent styling and smooth animations.
 */
export function ModalBackdrop({ transparent = false, className, children, ...props }: ModalBackdropProps) {
  return (
    <ModalOverlay
      {...props}
      className={composeRenderProps(className, (className, renderProps) => {
        return twMerge(
          // Base positioning and layering
          "fixed inset-0 z-50",
          // Remove focus outlines from modal overlay
          "outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0",
          // Backdrop styling - subtle gradient for depth
          transparent
            ? "bg-transparent"
            : [
                "bg-gradient-to-br from-black/20 via-black/15 to-black/20",
                "backdrop-blur-[2px]",
                "supports-[backdrop-filter]:bg-black/15",
              ].join(" "),
          // Smooth animations
          renderProps.isEntering && ["animate-in fade-in", "duration-300 ease-out"].join(" "),
          renderProps.isExiting && ["animate-out fade-out", "duration-200 ease-in"].join(" "),
          className
        );
      })}
    >
      {children}
    </ModalOverlay>
  );
}
